"use client";

import * as React from "react";
import { cn } from "../lib/utils";

// Contexto para expor onClose a componentes filhos (DialogContent usa pra render do X)
const DialogContext = React.createContext<{ onClose: () => void } | null>(null);

// ========== Dialog Root ==========

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /**
   * Se true, tentativas de fechar (click fora, Esc, botao X) primeiro
   * mostram confirmacao "Voce tem alteracoes nao salvas".
   * O pai controla — o Dialog nao inspeciona forms.
   */
  dirty?: boolean;
  /**
   * Callback opcional chamado quando o user escolhe "Salvar" no prompt de
   * alteracoes nao salvas. Se ausente, o prompt mostra so Descartar/Cancelar.
   * Deve executar a persistencia — o Dialog fecha automaticamente quando
   * a promise resolve.
   */
  onSave?: () => void | Promise<void>;
  /** Titulo do prompt de alteracoes nao salvas. */
  unsavedPromptTitle?: string;
  /** Texto do prompt de alteracoes nao salvas. */
  unsavedPromptMessage?: string;
  /** Label do botao Salvar no prompt. */
  unsavedPromptSaveLabel?: string;
  /** Label do botao Descartar no prompt. */
  unsavedPromptDiscardLabel?: string;
  /** Label do botao Cancelar no prompt. */
  unsavedPromptCancelLabel?: string;
}

export function Dialog({
  open,
  onOpenChange,
  children,
  dirty,
  onSave,
  unsavedPromptTitle = "Alterações não salvas",
  unsavedPromptMessage = "Você fez alterações que ainda não foram salvas. Deseja salvar antes de sair?",
  unsavedPromptSaveLabel = "Salvar",
  unsavedPromptDiscardLabel = "Descartar",
  unsavedPromptCancelLabel = "Cancelar",
}: DialogProps) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Fluxo unificado: qualquer tentativa de fechar passa por aqui.
  // Se dirty, abre o mini-prompt; senao, fecha direto.
  const attemptClose = React.useCallback(() => {
    if (dirty) {
      setConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  }, [dirty, onOpenChange]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Se o prompt de confirmacao ja esta aberto, Esc so fecha ele
        if (confirmOpen) {
          setConfirmOpen(false);
        } else {
          attemptClose();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, attemptClose, confirmOpen]);

  // Prevent body scroll
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  const contextValue = React.useMemo(
    () => ({ onClose: attemptClose }),
    [attemptClose]
  );

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave();
      setConfirmOpen(false);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setConfirmOpen(false);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <DialogContext.Provider value={contextValue}>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/*
         * Overlay + wrapper usam onMouseDown (nao onClick) porque um click
         * completo (down + up) que COMECA dentro do card e TERMINA fora
         * — tipico ao selecionar texto num input e arrastar o mouse pra
         * fora do modal — dispara click no ancestor comum, o wrapper,
         * fechando a modal sem intencao. Com mousedown, o disparo so
         * ocorre se o gesto EMPEZA fora do card. Como DialogContent tem
         * stopPropagation no mousedown, drags que iniciam la nunca chegam
         * aqui e nao fecham a modal por acidente.
         */}
        <div
          className="fixed inset-0 bg-black/50 animate-[fadeIn_0.15s_ease-out]"
          onMouseDown={attemptClose}
        />
        <div
          className="relative z-50 w-full max-h-[90vh] overflow-y-auto p-4"
          onMouseDown={(e) => {
            // So fecha se o mousedown foi diretamente no wrapper (padding
            // em volta do card). Sem essa checagem, um mousedown que
            // bubble do overlay tambem cairia aqui.
            if (e.target === e.currentTarget) attemptClose();
          }}
        >
          {children}
        </div>

        {/* Prompt de alteracoes nao salvas.
            Fica na mesma stacking context — z-60 pra ficar acima do
            conteudo mas o mesmo overlay do backdrop. */}
        {confirmOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmOpen(false)} />
            <div
              className="relative mx-auto w-full max-w-sm rounded-xl bg-card border border-border p-6 shadow-xl animate-[scaleIn_0.15s_ease-out]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground">{unsavedPromptTitle}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{unsavedPromptMessage}</p>
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={saving}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {unsavedPromptCancelLabel}
                </button>
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={saving}
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70 disabled:opacity-50"
                >
                  {unsavedPromptDiscardLabel}
                </button>
                {onSave && (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving && (
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {unsavedPromptSaveLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DialogContext.Provider>
  );
}

// ========== Dialog Content ==========

interface DialogContentProps {
  size?: "sm" | "md" | "lg" | "xl";
  children: React.ReactNode;
  className?: string;
  /** Oculta o botao X do canto superior direito. Default: false (mostra). */
  hideCloseButton?: boolean;
}

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

export function DialogContent({
  size = "md",
  children,
  className,
  hideCloseButton,
}: DialogContentProps) {
  const ctx = React.useContext(DialogContext);
  return (
    <div
      className={cn(
        "relative mx-auto w-full rounded-xl bg-card border border-border p-6 shadow-xl",
        "animate-[scaleIn_0.2s_ease-out]",
        sizeMap[size],
        className
      )}
      // Interrompe mousedown e click aqui — o wrapper e o overlay usam
      // mousedown pra decidir se fecham, entao precisamos parar os dois
      // pra qualquer gesto que EMPEZE no card (selecionar texto, focus,
      // etc) nao acionar close ao bubble.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {!hideCloseButton && ctx && (
        <button
          type="button"
          aria-label="Fechar"
          onClick={ctx.onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {children}
    </div>
  );
}

// ========== Dialog Header ==========

export function DialogHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  // pr-8 reserva espaco pro botao X do DialogContent (nao colide com titulo longo)
  return <div className={cn("mb-4 pr-8", className)}>{children}</div>;
}

// ========== Dialog Title ==========

export function DialogTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={cn("text-lg font-semibold text-foreground", className)}>{children}</h2>;
}

// ========== Dialog Description ==========

export function DialogDescription({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn("mt-1 text-sm text-muted-foreground", className)}>{children}</p>;
}

// ========== Dialog Footer ==========

export function DialogFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("mt-6 flex items-center justify-end gap-3", className)}>
      {children}
    </div>
  );
}

// ========== Confirm Dialog ==========

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "default";
  loading?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "danger",
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  const variantConfig = {
    danger: {
      iconBg: "bg-red-100 dark:bg-red-900/30",
      iconColor: "text-red-600 dark:text-red-400",
      buttonClass: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
    },
    warning: {
      iconBg: "bg-yellow-100 dark:bg-yellow-900/30",
      iconColor: "text-yellow-600 dark:text-yellow-400",
      buttonClass: "bg-yellow-600 text-white hover:bg-yellow-700 active:bg-yellow-800",
    },
    default: {
      iconBg: "bg-primary-100 dark:bg-primary-900/30",
      iconColor: "text-primary-600 dark:text-primary-400",
      buttonClass: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800",
    },
  };

  const config = variantConfig[variant];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <div className="flex gap-4">
          {/* Icon */}
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", config.iconBg)}>
            {variant === "danger" ? (
              <svg className={cn("h-5 w-5", config.iconColor)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ) : variant === "warning" ? (
              <svg className={cn("h-5 w-5", config.iconColor)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className={cn("h-5 w-5", config.iconColor)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
        </div>

        <DialogFooter className="mt-5">
          <button
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50",
              config.buttonClass
            )}
          >
            {loading && (
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
